const finite = (value, fallback) => ['number', 'string'].includes(typeof value) && String(value).trim() && Number.isFinite(Number(value)) ? Number(value) : fallback
const bounded = (value, fallback, min, max) => Math.max(min, Math.min(max, finite(value, fallback)))
const color = (value, fallback) => typeof value === 'string' && /^(?:#(?:[a-f\d]{3}|[a-f\d]{4}|[a-f\d]{6}|[a-f\d]{8})|(?:rgb|hsl)a?\([\d\s.,%+/-]+\)|transparent|white|black)$/i.test(value) ? value : fallback

export function linePresentationSettings (option = {}) {
  return { linePreset: option.linePreset === 'tech' ? 'tech' : 'classic',
    lineAccentColor: color(option.lineAccentColor, '#24d6ff'), lineSecondaryColor: color(option.lineSecondaryColor, '#3eebbd'),
    lineGridColor: color(option.lineGridColor, 'rgba(75, 146, 182, .22)'), lineAxisColor: color(option.lineAxisColor, '#6697b3'),
    lineTextColor: color(option.lineTextColor, '#c8e3f5'), lineFillOpacity: bounded(option.lineFillOpacity, .32, 0, 1),
    techLineWidth: bounded(option.techLineWidth, 2, 1, 10), techPointSize: bounded(option.techPointSize, 6, 2, 20),
    lineSmooth: option.lineSmooth !== false, lineFill: option.lineFill !== false, linePoints: option.linePoints !== false, lineGrid: option.lineGrid !== false }
}

export function lineAccessibleLabel (data = {}, option = {}, unit = '') {
  const series = (data.series || []).map(item => {
    const values = item.data || [], last = values[values.length - 1]
    const value = last && typeof last === 'object' ? last.value : last
    return (item.name || '数据') + '最新值 ' + (value == null || value === '' ? '—' : String(value)) + (unit ? ' ' + unit : '')
  })
  return (option.title || '折线图') + '；' + (series.length ? series.join('；') : '暂无数据')
}

// containLabel 只为刻度标签留白，不包含画在坐标区上方的轴名称。
// 最终 Option 合并后再按名称字号/行数补足顶部，避免高级配置或小边距重新裁掉单位。
export function fitLineAxisName (option, { height = 0 } = {}) {
  const axes = Array.isArray(option.yAxis) ? option.yAxis : [option.yAxis]
  const grids = Array.isArray(option.grid) ? option.grid : [option.grid || {}]
  const required = new Map()
  axes.forEach(axis => {
    if (!axis || axis.show === false || axis.name == null || !String(axis.name).trim()) return
    const location = axis.nameLocation || 'end'
    if (location !== (axis.inverse ? 'start' : 'end')) return
    const style = axis.nameTextStyle || {}, fontSize = Math.max(1, finite(style.fontSize, finite(option.textStyle && option.textStyle.fontSize, 12)))
    const lineHeight = Math.max(fontSize, finite(style.lineHeight, fontSize * 1.3))
    const padding = Array.isArray(style.padding) ? finite(style.padding[0], 0) + finite(style.padding[2] === undefined ? style.padding[0] : style.padding[2], 0) : finite(style.padding, 0) * 2
    const space = Math.ceil(Math.max(0, finite(axis.nameGap, 15)) + lineHeight * String(axis.name).split('\n').length + Math.max(0, padding) + 4)
    const index = axis.gridIndex || 0
    required.set(index, Math.max(required.get(index) || 0, space))
  })
  let changed = false
  const fitted = grids.map((grid, index) => {
    const space = required.get(index)
    if (!space) return grid
    const percent = typeof grid.top === 'string' && grid.top.match(/^\s*(\d+(?:\.\d+)?)%\s*$/)
    const current = percent ? Number(percent[1]) * Math.max(0, finite(height, 0)) / 100 : finite(grid.top, 60)
    if (current >= space) return grid
    changed = true
    return { ...grid, top: space }
  })
  return changed ? { ...option, grid: Array.isArray(option.grid) ? fitted : fitted[0] } : option
}

// 科技面积的边距已包含刻度所需位置，不再用 containLabel 把文字宽度重复加一次。
// 在同一次绘制前测量真实刻度；小边距和大数字仍保留足够空间，避免把留白问题变成文字裁切。
export function cartesianAxisLabelTexts (option, axis, dimension, index, engine) {
  let texts
  const formatter = axis.axisLabel && axis.axisLabel.formatter
  const format = (value, index, fallback = value) => typeof formatter === 'function' ? formatter(value, index) : typeof formatter === 'string' ? formatter.replace(/\{value\}/g, value) : fallback
  if (axis.type === 'category') texts = (axis.data || []).map((item, at) => format(item && typeof item === 'object' ? item.value : item, at))
  else if (!axis.type || axis.type === 'value') {
    const values = [], stacks = new Map()
    ;(option.series || []).filter(series => (series[dimension + 'AxisIndex'] || 0) === index).forEach(series => (series.data || []).forEach((item, at) => {
      const value = item && typeof item === 'object' && !Array.isArray(item) ? item.value : item
      const number = finite(Array.isArray(value) ? value[dimension === 'x' ? 0 : 1] : value, NaN)
      if (!Number.isFinite(number)) return
      values.push(number)
      if (series.stack) {
        const key = String(series.stack) + ':' + at + ':' + (number < 0 ? '-' : '+')
        stacks.set(key, (stacks.get(key) || 0) + number)
      }
    }))
    stacks.forEach(value => values.push(value))
    const extent = values.reduce((out, value) => [Math.min(out[0], value), Math.max(out[1], value)], [Infinity, -Infinity])
    const scale = engine && engine.helper && engine.helper.createScale && engine.helper.createScale(values.length ? extent : [0, 0], axis)
    texts = scale ? scale.getTicks().map((tick, at) => format(tick, at, scale.getLabel(tick))) : values.length ? [Math.min(0, extent[0]), Math.max(0, extent[1])].map(String) : ['0']
  } else return []
  return texts
}

export function fitLinePlotPadding (option, { width = 0, height = 0 } = {}, engine) {
  if (!engine || !engine.helper || !engine.helper.createScale || !engine.format || !engine.format.getTextRect) return option
  const axes = value => Array.isArray(value) ? value : value ? [value] : []
  const pixels = (value, size) => typeof value === 'string' && value.trim().endsWith('%') ? finite(value.slice(0, -1), 0) * size / 100 : finite(value, 0)
  const grids = axes(option.grid).map(grid => ({ ...grid }))
  let changed = false
  for (const dimension of ['x', 'y']) axes(option[dimension + 'Axis']).forEach((axis, index) => {
    const label = { ...axis.axisLabel && axis.axisLabel.textStyle, ...axis.axisLabel }, grid = grids[axis.gridIndex || 0]
    if (!grid || grid.containLabel === true || axis.show === false || label.show === false || label.inside) return
    const texts = cartesianAxisLabelTexts(option, axis, dimension, index, engine)
    if (!texts.length) return
    const fontSize = Math.max(1, finite(label.fontSize, 12)), font = [label.fontStyle || 'normal', label.fontWeight || 'normal', fontSize + 'px', label.fontFamily || 'sans-serif'].join(' ')
    const angle = finite(label.rotate, 0) * Math.PI / 180, margin = Math.max(0, finite(label.margin, 8)) + 2
    let needed = 0
    texts.forEach(text => {
      const rect = engine.format.getTextRect(text == null ? '' : String(text), font, undefined, undefined, label.padding, label.rich)
      needed = Math.max(needed, dimension === 'y' ? Math.abs(rect.width * Math.cos(angle)) + Math.abs(rect.height * Math.sin(angle)) : Math.abs(rect.height * Math.cos(angle)) + Math.abs(rect.width * Math.sin(angle)))
    })
    const side = axis.position || (dimension === 'y' ? 'left' : 'bottom'), size = dimension === 'y' ? width : height
    if (pixels(grid[side], size) < Math.ceil(needed + margin)) { grid[side] = Math.ceil(needed + margin); changed = true }
  })
  return changed ? { ...option, grid: Array.isArray(option.grid) ? grids : grids[0] } : option
}

// 沿用原系列及其真实数据，仅叠加可选外观；不生成示例点、不注册请求或计时器。
export function composeLinePresentation (generated, option = {}, unit = '') {
  const settings = linePresentationSettings(option)
  if (settings.linePreset !== 'tech') return generated
  const palette = [settings.lineAccentColor, settings.lineSecondaryColor, '#a5baff', '#ffc879']
  const axes = (value, vertical) => {
    const apply = axis => ({ ...axis, boundaryGap: axis.type === 'category' ? option.lineBoundaryGap === true : axis.boundaryGap,
      name: vertical ? option.yAxisName || unit || axis.name : axis.name,
      nameTextStyle: { ...axis.nameTextStyle, color: settings.lineTextColor, fontSize: vertical ? finite(option.yNameFontSize, 14) : finite(option.xNameFontSize, 14) },
      axisLine: { ...axis.axisLine, lineStyle: { ...axis.axisLine && axis.axisLine.lineStyle, color: settings.lineAxisColor, width: 1 } },
      axisTick: { ...axis.axisTick, show: false },
      axisLabel: { ...axis.axisLabel, color: settings.lineTextColor, textStyle: { ...axis.axisLabel && axis.axisLabel.textStyle, color: settings.lineTextColor } },
      splitLine: { show: settings.lineGrid, lineStyle: { color: settings.lineGridColor, width: 1, type: 'solid' } }
    })
    return Array.isArray(value) ? value.map(apply) : apply(value || {})
  }
  return { ...generated, backgroundColor: 'transparent',
    grid: { ...generated.grid, left: bounded(option.gridX, 44, 0, 400), right: bounded(option.gridX2, 18, 0, 400),
      top: Math.max(24, bounded(option.gridY, 24, 0, 400)), bottom: Math.max(20, bounded(option.gridY2, 28, 0, 400)), containLabel: false },
    xAxis: axes(generated.xAxis, false), yAxis: axes(generated.yAxis, true),
    tooltip: { ...generated.tooltip, backgroundColor: 'rgba(3, 28, 48, .96)', borderColor: settings.lineAxisColor, borderWidth: 1,
      textStyle: { ...generated.tooltip && generated.tooltip.textStyle, color: settings.lineTextColor }, axisPointer: { type: 'line', lineStyle: { color: settings.lineAxisColor, type: 'dashed' } } },
    legend: { ...generated.legend, textStyle: { ...generated.legend && generated.legend.textStyle, color: settings.lineTextColor },
      data: (generated.legend && generated.legend.data || []).map((item, index) => ({ ...(typeof item === 'object' ? item : { name: item }), textStyle: { color: palette[index % palette.length] } })) },
    series: (generated.series || []).map((series, index) => {
      const stroke = palette[index % palette.length]
      return { ...series, smooth: settings.lineSmooth, connectNulls: false, symbol: 'circle', showSymbol: settings.linePoints, symbolSize: settings.techPointSize,
        lineStyle: { ...series.lineStyle, color: stroke, width: settings.techLineWidth },
        itemStyle: { color: stroke, borderColor: stroke, borderWidth: 1 },
        areaStyle: settings.lineFill ? { opacity: settings.lineFillOpacity, color: { type: 'linear', x: 0, y: 0, x2: option.category ? 1 : 0, y2: option.category ? 0 : 1,
          colorStops: [{ offset: 0, color: stroke }, { offset: 1, color: 'rgba(0, 0, 0, 0)' }] } } : undefined }
    }) }
}

// 给可见点上数值保留必要边距，避免最高值接近轴上限时被组件边缘裁掉。
export function fitLineValueLabels (option, { width = 0, height = 0 } = {}, engine) {
  const list = value => Array.isArray(value) ? value : value ? [value] : []
  const grids = list(option.grid).map(grid => ({ ...grid })), required = new Map()
  ;(option.series || []).forEach(series => {
    if (series.type !== 'line' || series.showSymbol === false) return
    const x = list(option.xAxis)[series.xAxisIndex || 0], y = list(option.yAxis)[series.yAxisIndex || 0]
    const horizontal = y && y.type === 'category', gridIndex = (horizontal ? y : x) && (horizontal ? y : x).gridIndex || 0
    if (!grids[gridIndex]) return
    ;(series.data || []).forEach(datum => {
      const item = datum && typeof datum === 'object' && !Array.isArray(datum) ? datum : {}, raw = item.value === undefined ? datum : item.value
      const value = finite(Array.isArray(raw) ? raw[horizontal ? 0 : 1] : raw, NaN)
      if (!Number.isFinite(value)) return
      const label = { ...series.label && series.label.normal, ...series.label, ...item.label && item.label.normal, ...item.label }
      if (label.show !== true || !['top', 'bottom', 'left', 'right'].includes(label.position)) return
      const style = { ...label.textStyle, ...label }, fontSize = Math.max(1, finite(style.fontSize, 12)), font = [style.fontStyle || 'normal', style.fontWeight || 'normal', fontSize + 'px', style.fontFamily || 'sans-serif'].join(' ')
      let text = String(value)
      if (typeof label.formatter === 'string') text = label.formatter.replace(/\{c\}/g, text).replace(/\{a\}/g, series.name || '')
      const rect = engine && engine.format && engine.format.getTextRect ? engine.format.getTextRect(text, font, undefined, undefined, style.padding, style.rich) : { width: text.length * fontSize * .7, height: fontSize * 1.4 }
      const symbol = typeof series.symbolSize === 'number' ? Math.max(0, series.symbolSize) / 2 : 3
      const space = Math.ceil((['left', 'right'].includes(label.position) ? rect.width : rect.height) + Math.max(0, finite(label.distance, 5)) + symbol + 3)
      if (!required.has(gridIndex)) required.set(gridIndex, {})
      const sides = required.get(gridIndex); sides[label.position] = Math.max(sides[label.position] || 0, space)
    })
  })
  let changed = false
  grids.forEach((grid, index) => Object.entries(required.get(index) || {}).forEach(([side, space]) => {
    const size = ['left', 'right'].includes(side) ? width : height, current = typeof grid[side] === 'string' && grid[side].endsWith('%') ? finite(grid[side].slice(0, -1), 0) * size / 100 : finite(grid[side], 0)
    if (current < space) { grid[side] = space; changed = true }
  }))
  return changed ? { ...option, grid: Array.isArray(option.grid) ? grids : grids[0] } : option
}
